import React, { useState, useEffect } from 'react';
import { 
  Image as ImageIcon, X, MoveVertical, Camera, 
  Lightbulb, Star, Sun, Home, Eye, 
  Upload, Grid3X3, Info, CheckCircle2,
  AlertCircle, Sparkles
} from 'lucide-react';

const ImgPublish = ({ 
  formData, 
  handleImageUpload, 
  removeImage, 
  errors 
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(null);
  const [showTips, setShowTips] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(false);

  // Tips de fotografía profesional
  const photographyTips = [
    {
      icon: Sun,
      title: "Luz Natural",
      description: "Fotografía durante el día con luz natural. Evita el flash directo.",
      color: "yellow"
    },
    {
      icon: Eye,
      title: "Ángulos Estratégicos",
      description: "Captura desde las esquinas para mostrar la amplitud del espacio.",
      color: "blue"
    },
    {
      icon: Home,
      title: "Espacios Ordenados",
      description: "Asegúrate de que las habitaciones estén limpias y ordenadas.",
      color: "green"
    },
    {
      icon: Grid3X3,
      title: "Composición",
      description: "Usa la regla de los tercios para crear imágenes más atractivas.",
      color: "purple"
    }
  ];

  // Recomendaciones de fotos esenciales
  const essentialPhotos = [
    { name: "Fachada exterior", priority: "Alta", icon: Home },
    { name: "Sala principal", priority: "Alta", icon: Home },
    { name: "Cocina", priority: "Alta", icon: Home },
    { name: "Habitación principal", priority: "Alta", icon: Home },
    { name: "Baño principal", priority: "Media", icon: Home },
    { name: "Vista desde ventanas", priority: "Media", icon: Eye },
    { name: "Áreas exteriores", priority: "Baja", icon: Sun }
  ];

  // Función para reordenar las imágenes
  const reorderImages = (startIndex, endIndex) => {
    const newImages = [...formData.imagenes];
    const [movedImage] = newImages.splice(startIndex, 1);
    newImages.splice(endIndex, 0, movedImage);
    return newImages;
  };

  // Función para manejar el inicio del arrastre
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    if (e.dataTransfer.setDragImage) {
      const emptyDiv = document.createElement('div');
      emptyDiv.style.display = 'none';
      document.body.appendChild(emptyDiv);
      e.dataTransfer.setDragImage(emptyDiv, 0, 0);
      setTimeout(() => {
        document.body.removeChild(emptyDiv);
      }, 0);
    }
    e.dataTransfer.effectAllowed = "move";
  };

  // Función para manejar el arrastre sobre otro elemento
  const handleDragOver = (e, index) => {
    e.preventDefault();
    setIsDraggingOver(index);
  };

  // Función para manejar cuando se termina de arrastrar fuera de un elemento
  const handleDragLeave = () => {
    setIsDraggingOver(null);
  };

  // Función para manejar el drop (soltar)
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setIsDraggingOver(null);
      return;
    }

    const reorderedImages = reorderImages(draggedIndex, dropIndex);
    
    const event = new CustomEvent('imagesReordered', {
      detail: { images: reorderedImages }
    });
    document.dispatchEvent(event);
    
    setDraggedIndex(null);
    setIsDraggingOver(null);
  };

  // Manejar upload con feedback visual
  const handleImageUploadWithFeedback = (e) => {
    setUploadProgress(true);
    handleImageUpload(e);
    setTimeout(() => setUploadProgress(false), 1000);
  };

  // Configuración de colores para tips
  const getColorConfig = (color) => {
    const configs = {
      yellow: { bg: 'bg-yellow-50', icon: 'text-yellow-600', border: 'border-yellow-200' },
      blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
      green: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
      purple: { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'border-purple-200' }
    };
    return configs[color] || configs.blue;
  };

  return (
    <div className="space-y-8">
      {/* Header elegante */}
      <div className="text-center mb-8">
        <div className="relative mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-0.5 w-12 bg-gradient-to-r from-transparent to-pink-500 rounded-full"></div>
            <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl shadow-lg">
              <Camera className="w-6 h-6 text-white" />
            </div>
            <div className="h-0.5 w-12 bg-gradient-to-l from-transparent to-purple-500 rounded-full"></div>
          </div>
          
          <h2 className="text-3xl font-bold bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 
                         bg-clip-text text-transparent mb-3">
            Galería de Imágenes
          </h2>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
            <div className="w-16 h-0.5 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full"></div>
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-75"></div>
          </div>
        </div>
        
        <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
          Sube fotos de alta calidad para destacar tu propiedad
        </p>
      </div>

      {/* Tips de fotografía profesional */}
      {showTips && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Lightbulb className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-blue-800">Tips para Fotos Profesionales</h3>
                <p className="text-blue-600 text-sm">Maximiza el atractivo de tu propiedad</p>
              </div>
            </div>
            <button
              onClick={() => setShowTips(false)}
              className="p-2 hover:bg-blue-100 rounded-lg transition-colors duration-200"
            >
              <X className="w-5 h-5 text-blue-600" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {photographyTips.map((tip, index) => {
              const colors = getColorConfig(tip.color);
              return (
                <div
                  key={index}
                  className={`p-4 ${colors.bg} border ${colors.border} rounded-xl transition-all duration-300 hover:shadow-md`}
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <tip.icon className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">{tip.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fotos recomendadas */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Fotos Recomendadas
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {essentialPhotos.map((photo, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 p-2 rounded-lg text-sm
                    ${photo.priority === 'Alta' 
                      ? 'bg-red-50 text-red-700' 
                      : photo.priority === 'Media' 
                      ? 'bg-yellow-50 text-yellow-700' 
                      : 'bg-green-50 text-green-700'
                    }`}
                >
                  <photo.icon className="w-4 h-4" />
                  <span className="flex-1">{photo.name}</span>
                  <span className={`text-xs px-2 py-1 rounded-full
                    ${photo.priority === 'Alta' 
                      ? 'bg-red-100 text-red-600' 
                      : photo.priority === 'Media' 
                      ? 'bg-yellow-100 text-yellow-600' 
                      : 'bg-green-100 text-green-600'
                    }`}>
                    {photo.priority}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Área de carga principal */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-pink-50 to-purple-50 border-b border-gray-100 rounded-t-2xl">
          <div className="p-2 bg-gradient-to-br from-pink-100 to-pink-200 rounded-xl">
            <Upload className="h-6 w-6 text-pink-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Subir Imágenes</h3>
            <p className="text-gray-500 text-sm">
              {formData.imagenes.length} imagen{formData.imagenes.length !== 1 ? 'es' : ''} seleccionada{formData.imagenes.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="p-6">
          {/* Zona de drop elegante */}
          <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center 
                          hover:border-purple-400 hover:bg-purple-50/50 transition-all duration-300">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUploadWithFeedback}
              className="hidden"
              id="images"
            />
            
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl">
                  <ImageIcon className="w-12 h-12 text-purple-600" />
                </div>
              </div>
              
              <div>
                <label
                  htmlFor="images"
                  className="inline-flex items-center gap-2 cursor-pointer bg-gradient-to-r from-purple-600 to-pink-600 
                           text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg
                           hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  {uploadProgress ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>Seleccionar Imágenes</span>
                    </>
                  )}
                </label>
              </div>
              
              <div className="text-sm text-gray-500 space-y-1">
                <p>o arrastra y suelta las imágenes aquí</p>
                <p className="text-xs">Formatos: JPG, PNG, WEBP • Máximo 10MB por imagen</p>
              </div>
            </div>
          </div>

          {errors && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-700 text-sm">{errors}</p>
            </div>
          )}

          {/* Galería de imágenes */}
          {formData.imagenes.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Grid3X3 className="w-5 h-5" />
                  Vista Previa ({formData.imagenes.length})
                </h4>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <MoveVertical className="w-4 h-4" />
                  <span>Arrastra para reordenar</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {formData.imagenes.map((image, index) => (
                  <div
                    key={index}
                    className={`group relative bg-white rounded-2xl overflow-hidden shadow-lg border-2 transition-all duration-300
                      ${draggedIndex === index 
                        ? 'opacity-50 border-dashed border-purple-500 scale-105' 
                        : isDraggingOver === index 
                        ? 'border-purple-500 shadow-xl scale-105' 
                        : 'border-gray-200 hover:border-purple-300 hover:shadow-xl hover:scale-105'
                      }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={() => {
                      setDraggedIndex(null);
                      setIsDraggingOver(null);
                    }}
                  >
                    {/* Indicador de posición */}
                    <div className="absolute top-3 left-3 z-20">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm shadow-lg
                        ${index === 0 
                          ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white' 
                          : 'bg-black/70 text-white'
                        }`}>
                        {index === 0 ? <Star className="w-4 h-4" /> : index + 1}
                      </div>
                    </div>

                    {/* Imagen */}
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    </div>

                    {/* Overlay con controles */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent 
                                  opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      
                      {/* Botón eliminar */}
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 rounded-full 
                                 text-white shadow-lg transition-all duration-200 hover:scale-110"
                      >
                        <X className="w-4 h-4" />
                      </button>

                      {/* Indicador de arrastre */}
                      <div className="absolute bottom-3 right-3 p-2 bg-black/50 hover:bg-black/70 rounded-full 
                                    text-white cursor-move transition-all duration-200 hover:scale-110">
                        <MoveVertical className="w-4 h-4" />
                      </div>

                      {/* Badge de imagen principal */}
                      {index === 0 && (
                        <div className="absolute bottom-3 left-3 px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-500 
                                      rounded-full text-white text-xs font-semibold flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          <span>Principal</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Información adicional */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <Info className="w-4 h-4 text-blue-500" />
                  <span>
                    <strong>Imagen principal:</strong> La primera imagen será la portada de tu publicación. 
                    Asegúrate de que sea la más atractiva.
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImgPublish;